// Datetime helpers for the Promotion Plan promo window (Consolidation
// §2.2). The agency operates implicitly in Sydney time (cross-timezone
// handling is explicit future work — spec §11), so promo datetimes are
// authored, stored, and displayed as Sydney wall-clock time and persisted
// with a fixed +10:00 offset.
//
// Deliberately timezone-stable: we never round-trip through `Date` (which
// would reinterpret the instant in the browser/server's local zone and
// drift the displayed wall time). Instead we work on the ISO string parts
// directly, so what an AM types is exactly what's stored and read back —
// regardless of where the browser or server is.
//
// NOTE: AEST +10:00 is fixed. Sydney observes DST (+11:00 in summer); this
// is a known simplification carried from the spec's "Sydney (current
// implicit)" stance and called out in §11 as future work.

const SYDNEY_OFFSET = '+10:00';

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
// Capture the YYYY-MM-DDTHH:MM prefix of any fuller ISO datetime; seconds,
// milliseconds and the trailing offset are intentionally discarded — the
// prefix already is the authored Sydney wall time.
const DATETIME_PREFIX_RE = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/;

// Stored ISO (datetime with offset, or legacy date-only) → the value a
// native <input type="datetime-local"> expects ("YYYY-MM-DDTHH:MM").
// Legacy date-only data widens to midnight. Empty/null/garbage → "".
export function isoToDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  if (DATE_ONLY_RE.test(iso)) return `${iso}T00:00`;
  const m = DATETIME_PREFIX_RE.exec(iso);
  return m ? m[1]! : '';
}

// A native datetime-local value ("YYYY-MM-DDTHH:MM") → stored ISO with the
// fixed Sydney offset and zero seconds. Empty → null. A bare date (no time
// component) is treated as midnight Sydney.
export function datetimeLocalToIso(local: string): string | null {
  const v = local.trim();
  if (!v) return null;
  if (DATE_ONLY_RE.test(v)) return `${v}T00:00:00${SYDNEY_OFFSET}`;
  const m = DATETIME_PREFIX_RE.exec(v);
  if (!m) return null;
  return `${m[1]}:00${SYDNEY_OFFSET}`;
}

// Human display of a stored promo datetime in Sydney wall time, e.g.
// "1 May 2026, 9:00 AM". Legacy date-only renders without a time. Pure /
// locale-stable (hand-rolled, like src/lib/monthYear.ts) so server and
// client agree and a non-en locale can't reformat it.
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function formatSydneyDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const dateOnly = DATE_ONLY_RE.test(iso);
  const prefix = dateOnly ? `${iso}T00:00` : isoToDatetimeLocal(iso);
  if (!prefix) return iso;
  const [datePart, timePart] = prefix.split('T') as [string, string];
  const [y, mo, d] = datePart.split('-');
  const monthName = MONTHS[Number(mo) - 1] ?? mo;
  const day = String(Number(d));
  const dateStr = `${day} ${monthName} ${y}`;
  if (dateOnly) return dateStr;
  const [hStr, min] = timePart.split(':') as [string, string];
  let h = Number(hStr);
  const ampm = h < 12 ? 'AM' : 'PM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${dateStr}, ${h}:${min} ${ampm}`;
}
